import {
    Route,
    BrowserRouter as Router,
    Routes,
    Navigate
} from 'react-router-dom';
import './styles/App.css';
import { Toaster } from 'react-hot-toast';
import LoginPage from './pages/LoginPage.jsx';
import Header from './components/header/Header.jsx';
import HomePage from './pages/HomePage.jsx';
import OfficeNotesListPage from './pages/office-notes/OfficeNotesListPage.jsx';
import CreateOfficePage from './pages/office-notes/CreateOfficePage.jsx';
import ViewOfficeNotePage from './pages/office-notes/ViewOfficeNotePage.jsx';
import AdminAutomationsHomePage from './pages/admin-automations/AdminAutomationsHomePage.jsx';
import OacPage from './pages/admin-automations/OacPage.jsx';
import OarPage from './pages/admin-automations/OarPage.jsx';
import AoarPage from './pages/admin-automations/AoarPage.jsx';
import UserRemovalPage from './pages/admin-automations/UserRemovalPage.jsx';
import AoarEmailJobsPage from './pages/admin-automations/AoarEmailJobsPage.jsx';
import BpRemoval from './pages/admin-automations/BpRemoval.jsx';
import ComputerSystemRequirement from './pages/admin-automations/ComputerSystemRequirement.jsx';
import UserManagementPage from './pages/users/UserManagementPage.jsx';
import UsersHomePage from './pages/users/UsersHomePage.jsx';
import CreateUserPage from './pages/users/CreateUserPage.jsx';
import EditUserPage from './pages/users/EditUserPage.jsx';
import DeleteUserPage from './pages/users/DeleteUserPage.jsx';
import InstallFailsListPage from './pages/install-fails/InstallFailsListPage.jsx';
import HipaaHomePage from './pages/hipaa/HipaaHomePage.jsx';
import HipaaConcernsPage from './pages/hipaa/HipaaConcernsPage.jsx';
import HipaaCorrectionsPage from './pages/hipaa/HipaaCorrectionsPage.jsx';
import SendOsFailEmailToOfficePage from './pages/hipaa/SendOsFailEmailToOfficePage.jsx';
import NewScCardsListPage from './pages/new-sc-vetting-cards/NewScCardsListPage.jsx';
import CreateNewScPage from './pages/new-sc-vetting-cards/CreateNewScPage.jsx';
import AddUserToPlatformsPage from './pages/new-sc-vetting-cards/AddUserToPlatformsPage.jsx';
import ViewNewScCardPage from './pages/new-sc-vetting-cards/ViewNewScCardPage.jsx';
import ReportingDashboardPage from './pages/reporting/ReportingDashboardPage.jsx';
import TemporaryPage from './pages/TemporaryPage.jsx';
import WeeklyStatsPage from './pages/reporting/WeeklyStatsPage.jsx';
import QueryToolPage from './pages/reporting/QueryToolPage.jsx';
import AuditTicketPage from './pages/reporting/AuditTicketPage.jsx';
import AddTsPlusLicenseToComputerPage from './pages/office-notes/AddTsPlusLicenseToComputerPage.jsx';
import TsPlusLicensesListPage from './pages/ts-plus-licenses/TsPlusLicensesListPage.jsx';
import CreateUpdateTsPlusLicensePage from './pages/ts-plus-licenses/CreateUpdateTsPlusLicensePage.jsx';
import LicenseReportingDataPage from './pages/reporting/LicenseReportingDataPage.jsx';
import CreateUpdateManualTsPlusLicensePage from './pages/ts-plus-licenses/CreateUpdateManualTsPlusLicensePage.jsx';
import ScHomePage from './pages/new-sc-vetting-cards/ScHomePage.jsx';
import Fishbowl from './pages/new-sc-vetting-cards/Fishbowl.jsx';
import ScDashboard from './pages/new-sc-vetting-cards/ScDashboard.jsx';
import COSAddSikkaCreds from './components/office-notes/COSAddSikkaCreds.jsx';
import CopilotWeeklyReportPage from './pages/reporting/CopilotWeeklyReportPage.jsx';
import TeamTasksPage from './pages/team-tasks/TeamTasksPage.jsx';
import TaskReportPage from './pages/team-tasks/TaskReportPage.jsx';
import MissionSubmitPage from './pages/mission-requests/MissionSubmitPage.jsx';
import MyMissionRequestsPage from './pages/mission-requests/MyMissionRequestsPage.jsx';
import MissionReviewPage from './pages/mission-requests/MissionReviewPage.jsx';
import ExecutiveDashboard from './pages/ExecutiveDashboard.jsx';
import ExecutivePortalChrome from './components/executive-pulse-dashboard/ExecutivePortalChrome.jsx';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import useCurrentUser from './hooks/useCurrentUser.js';
import { isExecOnlyOracleViewer } from './utils/executive-viewer-profile.js';

/**
 * Logged-in routes: executives with Pulse access but no IT role see only the simple
 * Executive bar; IT (team / lead / leadership) keeps the full header everywhere.
 */
function AuthenticatedShell() {
    const { user, loading: userLoading } = useCurrentUser();
    const execOnly = Boolean(user) && isExecOnlyOracleViewer(user);

    let chrome;
    if (userLoading) {
        chrome = <div className="app-chrome-skeleton" aria-hidden="true" />;
    } else if (execOnly) {
        chrome = <ExecutivePortalChrome />;
    } else {
        chrome = <Header />;
    }

    const mainClassName =
        userLoading || !execOnly
            ? 'app-main app-main--oracle-header'
            : 'app-main';

    return (
        <>
            {chrome}
            <div className={mainClassName}>
                {userLoading ? (
                    <LoadingSpinner />
                ) : (
                    <Routes>
                        <Route
                            path="/executive-pulse/"
                            element={
                                <Navigate
                                    to="/executive-pulse/dashboard"
                                    replace
                                />
                            }
                        />
                        <Route
                            path="/executive-pulse"
                            element={
                                <Navigate
                                    to="/executive-pulse/dashboard"
                                    replace
                                />
                            }
                        />
                        <Route
                            path="/executive-pulse/dashboard"
                            element={
                                <ExecutiveDashboard portalMode={execOnly} />
                            }
                        />

                        {/* Home page — non-IT executives go straight to Executive Pulse */}
                        <Route
                            path="/"
                            element={
                                execOnly ? (
                                    <Navigate
                                        to="/executive-pulse/dashboard"
                                        replace
                                    />
                                ) : (
                                    <HomePage />
                                )
                            }
                        />

                        {/* Office note pages */}
                        <Route
                            path="/notes"
                            element={<OfficeNotesListPage />}
                        />
                        <Route
                            path="/createOffice"
                            element={<CreateOfficePage />}
                        />
                        <Route
                            path="/office/:officeId"
                            element={<ViewOfficeNotePage />}
                        />
                        <Route
                            path="/addTsPlusLicense"
                            element={<AddTsPlusLicenseToComputerPage />}
                        />
                        <Route path="/sikka" element={<COSAddSikkaCreds />} />

                        {/* Admin automatin pages */}
                        <Route
                            path="/admin"
                            element={<AdminAutomationsHomePage />}
                        />
                        <Route path="/oac" element={<OacPage />} />
                        <Route path="/oar" element={<OarPage />} />
                        <Route path="/aoar" element={<AoarPage />} />
                        <Route
                            path="/userRemoval"
                            element={<UserRemovalPage />}
                        />
                        <Route
                            path="/emailJobs"
                            element={<AoarEmailJobsPage />}
                        />
                        <Route path="/bpRemoval" element={<BpRemoval />} />
                        <Route
                            path="/csr"
                            element={<ComputerSystemRequirement />}
                        />

                        {/* User management pages */}
                        <Route
                            path="/manage-users"
                            element={<UserManagementPage />}
                        />
                        <Route path="/users" element={<UsersHomePage />} />
                        <Route
                            path="/createuser"
                            element={<CreateUserPage />}
                        />
                        <Route path="/updateuser" element={<EditUserPage />} />
                        <Route
                            path="/deleteuser"
                            element={<DeleteUserPage />}
                        />

                        {/* Install fails pages */}
                        <Route
                            path="/installfails"
                            element={<InstallFailsListPage />}
                        />

                        {/* HIPAA pages */}
                        <Route path="/hipaa" element={<HipaaHomePage />} />
                        <Route
                            path="/hipaaconcerns"
                            element={<HipaaConcernsPage />}
                        />
                        <Route
                            path="/hipaacorrections"
                            element={<HipaaCorrectionsPage />}
                        />
                        <Route
                            path="/hipaaOsEmail"
                            element={<SendOsFailEmailToOfficePage />}
                        />

                        {/* New SC Vetting Card pages */}
                        <Route path="/newsc" element={<ScHomePage />} />
                        <Route
                            path="/scCards"
                            element={<NewScCardsListPage />}
                        />
                        <Route path="/scDashboard" element={<ScDashboard />} />
                        <Route path="/fishbowl" element={<Fishbowl />} />
                        <Route
                            path="/addVettingCard"
                            element={<CreateNewScPage />}
                        />
                        <Route
                            path="/addSC"
                            element={<AddUserToPlatformsPage />}
                        />
                        <Route
                            path="/newsc/:cardId"
                            element={<ViewNewScCardPage />}
                        />

                        {/* Reporting pages */}
                        <Route
                            path="/reporting"
                            element={<ReportingDashboardPage />}
                        />
                        <Route
                            path="/executive-dashboard"
                            element={
                                <ExecutiveDashboard portalMode={execOnly} />
                            }
                        />
                        <Route
                            path="/ncltweeklystats"
                            element={
                                <WeeklyStatsPage title="NOI" itSegment="nclt" />
                            }
                        />
                        <Route
                            path="/hdWeeklyStats"
                            element={
                                <WeeklyStatsPage title="HD" itSegment="hd" />
                            }
                        />
                        <Route
                            path="/newscweeklystats"
                            element={
                                <WeeklyStatsPage
                                    title="New SC Install/Admin"
                                    itSegment="newsc/admin"
                                />
                            }
                        />
                        <Route
                            path="/copilotWeeklyReport"
                            element={<CopilotWeeklyReportPage />}
                        />
                        <Route path="/queryTool" element={<QueryToolPage />} />
                        <Route
                            path="/auditTickets"
                            element={<AuditTicketPage />}
                        />
                        <Route
                            path="/licenseReporting"
                            element={<LicenseReportingDataPage />}
                        />

                        {/* Team Tasks Dashboard */}
                        <Route path="/tasks" element={<TeamTasksPage />} />
                        <Route
                            path="/tasks/report"
                            element={<TaskReportPage />}
                        />

                        {/* Mission Requests */}
                        <Route
                            path="/mission-requests/submit"
                            element={<MissionSubmitPage />}
                        />
                        <Route
                            path="/mission-requests/my"
                            element={<MyMissionRequestsPage />}
                        />
                        <Route
                            path="/mission-requests/review"
                            element={<MissionReviewPage />}
                        />

                        {/* TSplus licenses pages */}
                        <Route
                            path="/licenses"
                            element={<TsPlusLicensesListPage />}
                        />
                        <Route
                            path="/editLicense"
                            element={<CreateUpdateTsPlusLicensePage />}
                        />
                        <Route
                            path="/addManualLicense"
                            element={<CreateUpdateManualTsPlusLicensePage />}
                        />

                        {/* Temporary page */}
                        <Route path="/temporary" element={<TemporaryPage />} />
                    </Routes>
                )}
            </div>
        </>
    );
}

function App() {
    const accesstoken = sessionStorage.getItem('accessToken');
    const refreshToken = sessionStorage.getItem('refreshToken');

    // If not logged in, load login screen
    if (!accesstoken || !refreshToken) {
        return (
            <Router>
                <Routes>
                    <Route
                        path="/executive-pulse"
                        element={<LoginPage executivePortal />}
                    />
                    <Route
                        path="/executive-pulse/*"
                        element={<LoginPage executivePortal />}
                    />
                    <Route path="/*" element={<LoginPage />} />
                </Routes>
            </Router>
        );
    }

    return (
        <div className="app">
            <Router>
                <AuthenticatedShell />
                <Toaster position="top-right" reverseOrder={false} />
            </Router>
        </div>
    );
}

export default App;
